if (typeof WolforkSubscriptionToggle !== 'function') {

  class WolforkSubscriptionToggle extends HTMLElement {

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      try {
        this.plans = JSON.parse(this.dataset.plansJson || '[]');
        this.variants = JSON.parse(this.dataset.variantsJson || '[]');
      } catch (err) {
        console.warn('[wf-sub-toggle] failed to parse JSON data', err);
        this.plans = [];
        this.variants = [];
      }
      // Onveranderde kopie: _setPlansForVariant overschrijft this.plans.
      this.allPlans = this.plans.slice();

      try {
        this.plansByVariant = JSON.parse(this.dataset.plansByVariantJson || '{}');
      } catch (err) {
        this.plansByVariant = {};
      }

      this.discountPct = parseFloat(this.dataset.discountPct) || 0;
      this.discountBadgeTemplate = this.dataset.discountBadge || '';
      // 'off' | 'quiz' | 'always'. Bij 'off' stuurt Liquid de intro-plannen niet
      // eens mee, dus dan is er hier niets te kiezen.
      this.introMode = this.dataset.introMode || 'off';
      this.productHandle = this.dataset.productHandle || '';
      // Product-ID is in elke taal gelijk; de handle niet (/en/ heeft een vertaalde handle).
      this.productId = this.dataset.productId || '';
      // Serverzijdig oordeel (Liquid): 'tag' | 'blocked' | 'cart' | ''.
      // Is dit gevuld, dan staan de intro-plannen niet eens in de HTML.
      this.introBlocked = this.dataset.introBlocked || '';
      // Serverzijdig: ingelogde klant heeft een hondprofiel met deze receptuur.
      this.introAdvisedServer = this.dataset.introAdvised === 'true';
      this.customerLoggedIn = this.dataset.customerLoggedIn === 'true';
      this.eligibilityUrl = this.dataset.eligibilityUrl || '';
      // Oordeel van de Worker (laag 1b): null = nog niet gevraagd of onderweg.
      this.remoteVerdict = null;
      this.minGrams = parseInt(this.dataset.minGrams, 10) || 20;
      this.maxGrams = parseInt(this.dataset.maxGrams, 10) || 2000;
      this.moneyFormat = this.dataset.moneyFormat || '€{{amount}}';
      this.i18n = {
        helperEnter: this.dataset.i18nHelperEnter || 'Enter your daily amount to determine your delivery schedule.',
        helperRequired: this.dataset.i18nHelperRequired || 'Enter grams per day',
        helperRange: this.dataset.i18nHelperRange || 'Enter a value between [min] and [max].',
        deliveryEvery: this.dataset.i18nDeliveryEvery || 'Delivery every [freq]',
        cyclePricing: this.dataset.i18nCyclePricing || 'First cycle [first], then [recurring]',
        frequencyDay: this.dataset.i18nFrequencyDay || 'day',
        frequencyWeek: this.dataset.i18nFrequencyWeek || 'week',
        frequencyWeeks: this.dataset.i18nFrequencyWeeks || '[count] weeks',
        frequencyDays: this.dataset.i18nFrequencyDays || '[count] days'
      };

      this.formId = this.dataset.form;
      this.form = this.formId ? document.getElementById(this.formId) : null;

      this.option = this.dataset.defaultOption === 'subscription' ? 'subscription' : 'onetime';

      // Hydratie van dailyGrams: URL param > customer metafield > localStorage > leeg
      // URL param ?grams=XXX wint zodat de Cybox quiz return-flow werkt zonder app-koppeling
      const fromUrl = this._readGramsFromUrl();
      const recommended = parseInt(this.dataset.recommendedGrams, 10);
      const stored = parseInt(localStorage.getItem('wolfork.daily_grams'), 10);

      if (fromUrl !== null) {
        this.dailyGrams = fromUrl;
        this.option = 'subscription';
        try { localStorage.setItem('wolfork.daily_grams', String(fromUrl)); } catch (_) {}
        // ?grams= komt alleen van de quiz-redirect of de resultaatpagina, en die
        // sturen naar het product dat geadviseerd is. Dat is dus het signaal
        // "de quiz adviseerde DEZE receptuur", en dat onthouden we per handle.
        this._markQuizAdvised(this.productHandle);
        this._clearEligibilityCache();
        this._cleanUrlParam();
      } else if (!isNaN(recommended) && recommended > 0) {
        this.dailyGrams = recommended;
      } else if (!isNaN(stored) && stored > 0) {
        this.dailyGrams = stored;
      } else {
        this.dailyGrams = null;
      }

      // ?intro=0 komt uit de mail na een geweigerde intro-bestelling. De klant
      // komt dan op hetzelfde product met het gewone abonnement, en dat blijft zo:
      // we onthouden de receptuur net als na een gewone intro-bestelling.
      if (this._readIntroOffFromUrl()) {
        this.option = 'subscription';
        this._markIntroOrdered(this.productHandle);
        this._cleanIntroParam();
      }

      // Sync radio button DOM state to this.option (URL kan option op subscription zetten
      // terwijl de gerenderde HTML default nog onetime checked heeft).
      this.querySelectorAll('[data-js-option-radio]').forEach(input => {
        input.checked = input.value === this.option;
      });

      this.variantId = this._findCurrentVariantId();
      this._setPlansForVariant();
      this.variantGrams = this._getVariantGrams(this.variantId);
      this.variantPriceCents = this._getVariantPrice(this.variantId);

      // Aantal zakken: meer zakken = langere voorraad = langere bezorgfrequentie
      this.quantityInput = this.form
        ? this.form.querySelector('.qty-selector') || this.form.querySelector('input[name="quantity"]')
        : null;
      this.quantity = this._readQuantity();

      this._hydrateGramsInput();
      this._refreshSelectedPlan();
      this._renderOptionState();
      this._renderPrices();
      this._renderGramsRowVisibility();
      this._renderQuizLink();
      this._renderHelper();
      this._syncSellingPlanInput();
      this._bindEvents();
      // Laag 1b: oordeel van de Worker ophalen (async). Herrendert alleen als het
      // iets verandert; tot die tijd staat de lokale beslissing.
      this._loadRemoteEligibility();
      this._refreshCartIntroState();
    }

    disconnectedCallback() {
      document.removeEventListener('krown:variant:change', this._onVariantChange);
      if (this.form) {
        this.form.removeEventListener('submit', this._onFormSubmit, true);
      }
      if (this.quantityInput) {
        this.quantityInput.removeEventListener('change', this._onQuantityChange);
      }
    }

    _bindEvents() {
      this.querySelectorAll('[data-js-option-radio]').forEach(input => {
        input.addEventListener('change', this._onOptionChange);
      });

      const gramsInput = this.querySelector('[data-js-grams-input]');
      if (gramsInput) {
        gramsInput.addEventListener('input', this._onGramsInput);
        gramsInput.addEventListener('blur', this._onGramsBlur);
      }

      this.planSelect = this.querySelector('[data-js-plan-select]');
      if (this.planSelect) {
        this.planSelect.addEventListener('change', this._onPlanChange);
      }

      if (this.quantityInput) {
        this.quantityInput.addEventListener('change', this._onQuantityChange);
      }

      if (this.form) {
        this.form.addEventListener('submit', this._onFormSubmit, true);
      }

      document.addEventListener('krown:variant:change', this._onVariantChange);
      document.addEventListener('krown:cart:add', this._onCartChanged);
      document.addEventListener('krown:cart:update', this._onCartChanged);
    }

    _onCartChanged = () => {
      this._refreshCartIntroState();
    };

    _onOptionChange = (e) => {
      this._setOption(e.target.value);
    };

    _onFormSubmit = (event) => {
      if (!this._subscriptionNeedsGrams()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this._renderHelper();
      const input = this.querySelector('[data-js-grams-input]');
      if (input) input.focus();
    };

    _onGramsInput = (e) => {
      const raw = e.target.value.trim();
      const parsed = parseInt(raw, 10);
      if (raw === '' || isNaN(parsed)) {
        this.dailyGrams = null;
        try { localStorage.removeItem('wolfork.daily_grams'); } catch (_) {}
      } else {
        this.dailyGrams = parsed;
        if (parsed >= this.minGrams && parsed <= this.maxGrams) {
          try { localStorage.setItem('wolfork.daily_grams', String(parsed)); } catch (_) {}
        }
      }
      this.manualPlanId = null;
      this._refreshSelectedPlan();
      this._renderPrices();
      this._syncSellingPlanInput();
      this._renderHelper();
      this._renderQuizLink();
    };

    _onGramsBlur = () => {
      this._renderHelper();
    };

    _readQuantity() {
      const v = this.quantityInput ? parseInt(this.quantityInput.value, 10) : 1;
      return (!isNaN(v) && v > 0) ? v : 1;
    }

    _onQuantityChange = () => {
      this.quantity = this._readQuantity();
      this.manualPlanId = null;
      this._refreshSelectedPlan();
      this._renderPrices();
      this._syncSellingPlanInput();
      this._renderHelper();
    };

    _onVariantChange = (e) => {
      const variant = e?.detail?.variant;
      if (!variant) return;
      this.variantId = variant.id;
      this.manualPlanId = null;
      this._setPlansForVariant();
      this.variantGrams = this._getVariantGrams(variant.id) || variant.grams || 0;
      this.variantPriceCents = this._getVariantPrice(variant.id) || variant.price || 0;
      this._refreshSelectedPlan();
      this._renderOptionState();
      this._renderPrices();
      this._syncSellingPlanInput();
      this._renderHelper();
    };

    _onPlanChange = (e) => {
      const planId = parseInt(e.target.value, 10);
      const plan = this.plans.find(item => item.id === planId);
      if (!plan) return;
      this.manualPlanId = plan.id;
      this.selectedPlan = plan;
      this._renderPrices();
      this._renderHelper();
      this._syncSellingPlanInput();
    };

    _setOption(opt) {
      this.option = opt;
      this.querySelectorAll('[data-js-option-radio]').forEach(input => {
        input.checked = input.value === opt;
      });
      this._renderOptionState();
      this._renderGramsRowVisibility();
      this._renderQuizLink();
      this._renderHelper();
      this._syncSellingPlanInput();
      this._renderMainPrice();
      this._renderCartButtonPrice();
      this.dispatchEvent(new CustomEvent('wolfork:subscription-changed', {
        bubbles: true,
        detail: {
          option: this.option,
          plan: this.selectedPlan,
          variantId: this.variantId,
          priceCents: this.option === 'subscription' && this.selectedPlan
            ? this._subscriptionPriceCents()
            : this.variantPriceCents
        }
      }));
    }

    // ---- Laag 1, browserkant -------------------------------------------------
    // De quiz laat per geadviseerde receptuur (producthandle) een spoor achter in
    // localStorage, met een cookie als backup voor browsers die localStorage
    // blokkeren. Die uitslag bestaat alleen in de browser, dus deze afweging kan
    // niet in Liquid. Zelfde sleutels als quiz-engine.js markCompleted().
    // Recepturen die in deze browser al als intro zijn besteld. Zelfde opslag als
    // de custom pixel "Wolfork intro besteld" na de checkout schrijft.
    _readIntroOrderedHandles() {
      const set = new Set();
      try {
        const raw = localStorage.getItem('wolfork.intro_ordered');
        if (raw) JSON.parse(raw).forEach(h => h && set.add(String(h)));
      } catch (_) {}
      try {
        const m = document.cookie.match(/(?:^|;\s*)wolfork_intro_ordered=([^;]*)/);
        if (m) decodeURIComponent(m[1]).split(',').forEach(h => h && set.add(h));
      } catch (_) {}
      return set;
    }

    _introOrderedHere() {
      return !!this.productHandle && this._readIntroOrderedHandles().has(this.productHandle);
    }

    _markIntroOrdered(handle) {
      if (!handle) return;
      const set = this._readIntroOrderedHandles();
      set.add(handle);
      const list = Array.from(set);
      try { localStorage.setItem('wolfork.intro_ordered', JSON.stringify(list)); } catch (_) {}
      try {
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = 'wolfork_intro_ordered=' + encodeURIComponent(list.join(',')) +
          '; path=/; max-age=' + (365 * 24 * 3600) + '; SameSite=Lax' + secure;
      } catch (_) {}
    }

    _readIntroOffFromUrl() {
      try {
        return new URLSearchParams(window.location.search).get('intro') === '0';
      } catch (_) {
        return false;
      }
    }

    _cleanIntroParam() {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('intro');
        window.history.replaceState({}, '', url.toString());
      } catch (_) {}
    }

    _readAdvisedHandles() {
      const set = new Set();
      try {
        const raw = localStorage.getItem('wolfork.quiz_recipes');
        if (raw) JSON.parse(raw).forEach(h => h && set.add(String(h)));
      } catch (_) {}
      try {
        const m = document.cookie.match(/(?:^|;\s*)wolfork_quiz_recipes=([^;]*)/);
        if (m) decodeURIComponent(m[1]).split(',').forEach(h => h && set.add(h));
      } catch (_) {}
      return set;
    }

    _markQuizAdvised(handle) {
      if (!handle) return;
      const set = this._readAdvisedHandles();
      set.add(handle);
      const list = Array.from(set);
      try { localStorage.setItem('wolfork.quiz_recipes', JSON.stringify(list)); } catch (_) {}
      try { localStorage.setItem('wolfork.quiz_completed', '1'); } catch (_) {}
      try {
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        const maxAge = '; max-age=' + (180 * 24 * 3600);
        document.cookie = 'wolfork_quiz_recipes=' + encodeURIComponent(list.join(',')) + '; path=/' + maxAge + '; SameSite=Lax' + secure;
        document.cookie = 'wolfork_quiz_done=1; path=/' + maxAge + '; SameSite=Lax' + secure;
      } catch (_) {}
    }

    // "Is de quiz uberhaupt afgerond", los van welke receptuur. Bepaalt alleen of
    // de quiz-knop nog nut heeft; voor het intro-abonnement telt de receptuur wel.
    _quizCompletedAnywhere() {
      if (this.introAdvisedServer) return true;
      try { if (localStorage.getItem('wolfork.quiz_completed') === '1') return true; } catch (_) {}
      try { if (/(^|;\s*)wolfork_quiz_done=1/.test(document.cookie)) return true; } catch (_) {}
      return this._readAdvisedHandles().size > 0;
    }

    // De quiz-knop is een uitweg voor wie de dagdosis niet weet. Na een eerdere
    // quiz blijft hij verborgen zolang er een dosis staat, maar bij leegmaken
    // verschijnen "of" en de knop meteen opnieuw.
    _renderQuizLink() {
      const hideQuizLink = this._quizCompletedAnywhere() && !!this.dailyGrams;
      ['[data-js-quiz-link]', '[data-js-quiz-or]'].forEach(sel => {
        const el = this.querySelector(sel);
        if (!el) return;
        if (hideQuizLink) {
          el.setAttribute('hidden', '');
        } else {
          el.removeAttribute('hidden');
        }
      });
    }

    // "De quiz adviseerde DEZE receptuur." Een afgeronde quiz voor een andere
    // receptuur telt niet; daar is het advies immers niet voor.
    _quizAdvisedThisProduct() {
      if (this.introAdvisedServer) return true;
      if (!this.productHandle) return false;
      return this._readAdvisedHandles().has(this.productHandle);
    }

    _useIntro() {
      // Serverzijdig geblokkeerd (tag, intro-blocked of al in de cart): nooit.
      if (this.introBlocked) return false;
      // Zelfde regel, maar live: zodra er een intro-regel voor dit product in de
      // winkelwagen ligt, verdwijnt de intro zonder dat de pagina herlaadt.
      if (this.cartHasIntro) return false;
      if (this.introMode === 'off') return false;
      // Oordeel van de Worker op e-mail of klant-id (tags + hondprofielen). Zolang
      // het onderweg is houden we de lokale beslissing aan; het oordeel kan daarna
      // alleen nog verlagen. Een storing of ontbrekend endpoint blokkeert nooit.
      if (this._remoteBlocksThisProduct() === true) return false;
      // Deze browser heeft deze receptuur al eens als intro besteld (gezet door de
      // custom pixel na de bestelling, of via de link in de annuleringsmail).
      // Voor gasten is dit het enige wat we vooraf weten; ingelogde klanten
      // worden daarnaast serverzijdig op hun tag gecontroleerd.
      if (this._introOrderedHere()) return false;
      if (this.introMode === 'always') return true;
      return this._quizAdvisedThisProduct();
    }

    // ---- Een intro per receptuur, ook binnen dezelfde paginabezoek -----------
    // Liquid blokkeert de intro al als de winkelwagen er een bevat, maar dat
    // oordeel is van het moment dat de pagina werd geladen. Zonder deze controle
    // kan iemand twee keer op "in winkelmand" drukken en stapelt Shopify het tot
    // aantal 2 op dezelfde regel: twee introboxen in een bestelling.
    _introPlanIds() {
      const ids = new Set();
      const add = list => (list || []).forEach(p => { if (p && p.is_intro) ids.add(String(p.id)); });
      if (this.plansByVariant) Object.values(this.plansByVariant).forEach(add);
      add(this.allPlans);
      return ids;
    }

    async _refreshCartIntroState() {
      if (!this.productHandle) return;
      const introIds = this._introPlanIds();
      if (!introIds.size) return;
      let cart;
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        cart = await res.json();
      } catch (_) {
        return; // Netwerkfout mag de keuze niet beinvloeden.
      }
      const found = (cart.items || []).some(item => {
        // Op ID: /cart.js geeft altijd de NL-handle, ook op de Engelse pagina.
        const sameProduct = this.productId
          ? String(item.product_id) === this.productId
          : item.handle === this.productHandle;
        if (!sameProduct) return false;
        const planId = item.selling_plan_allocation
          && item.selling_plan_allocation.selling_plan
          && item.selling_plan_allocation.selling_plan.id;
        return planId != null && introIds.has(String(planId));
      });
      if (found === this.cartHasIntro) return;
      this.cartHasIntro = found;
      this._applyEligibility();
    }

    // Een intro-abonnement is er een per bestelling, dus het aantal staat vast op 1.
    _lockQuantityForIntro() {
      if (!this.quantityInput) return;
      const lock = this.option === 'subscription' && this.selectedPlan && !!this.selectedPlan.is_intro;
      if (lock) {
        if (this.quantityInput.value !== '1') {
          this.quantityInput.value = '1';
          this.quantity = 1;
        }
        this.quantityInput.setAttribute('readonly', '');
        this.quantityInput.dataset.wfIntroLock = '';
      } else if (this.quantityInput.dataset.wfIntroLock !== undefined) {
        this.quantityInput.removeAttribute('readonly');
        delete this.quantityInput.dataset.wfIntroLock;
      }
    }

    // ---- Laag 1b: oordeel van de Worker --------------------------------------
    _quizEmail() {
      try {
        const stored = localStorage.getItem('wolfork.quiz_email');
        if (stored) return String(stored).trim().toLowerCase();
      } catch (_) {}
      try {
        const raw = localStorage.getItem('wolfork_quiz_state');
        if (raw) {
          const state = JSON.parse(raw);
          const email = state && state.answers && state.answers.email;
          if (email) return String(email).trim().toLowerCase();
        }
      } catch (_) {}
      return '';
    }

    _remoteBlocksThisProduct() {
      const v = this.remoteVerdict;
      if (!v) return null;
      if (v.block_all) return true;
      return (v.blocked_recipes || []).includes(this.productHandle);
    }

    // De hondnaam hoort in de sleutel: het oordeel van de Worker hangt ervan af.
    // Zonder de naam kreeg een nieuwe hond zes uur lang het bewaarde oordeel van
    // de vorige hond, zonder dat de Worker opnieuw gevraagd werd.
    _eligibilityCacheKey(identity) {
      const dog = String(this._quizDogName() || '').trim().toLowerCase().replace(/\s+/g, ' ');
      return 'wolfork.intro_eligibility:' + identity + '|' + this.productHandle + '|' + dog;
    }

    // Een vers quizadvies maakt elk bewaard oordeel voor dit product verouderd.
    _clearEligibilityCache() {
      try {
        const prefix = 'wolfork.intro_eligibility:';
        const marker = '|' + this.productHandle;
        Object.keys(localStorage)
          .filter(k => k.startsWith(prefix) && k.includes(marker))
          .forEach(k => localStorage.removeItem(k));
      } catch (_) {}
    }

    _readEligibilityCache(identity) {
      try {
        const raw = localStorage.getItem(this._eligibilityCacheKey(identity));
        if (!raw) return null;
        const v = JSON.parse(raw);
        if (!v || typeof v.ts !== 'number') return null;
        if (Date.now() - v.ts > 6 * 3600 * 1000) return null;
        return v;
      } catch (_) {
        return null;
      }
    }

    _writeEligibilityCache(identity, verdict) {
      try {
        localStorage.setItem(this._eligibilityCacheKey(identity), JSON.stringify({ ...verdict, ts: Date.now() }));
      } catch (_) {}
    }

    _hasAnyIntroPlan() {
      if (this.plansByVariant && Object.keys(this.plansByVariant).length) {
        return Object.values(this.plansByVariant).some(list => (list || []).some(p => p && p.is_intro));
      }
      return (this.allPlans || []).some(p => p && p.is_intro);
    }

    _applyEligibility() {
      this._setPlansForVariant();
      this._refreshSelectedPlan();
      this._renderPrices();
      this._syncSellingPlanInput();
      this._renderHelper();
    }

    async _loadRemoteEligibility() {
      const skip = { skipped: true, blocked_recipes: [], block_all: false };
      if (!this.eligibilityUrl || this.introMode === 'off' || this.introBlocked || !this._hasAnyIntroPlan()) {
        this.remoteVerdict = skip;
        return;
      }
      const email = this.customerLoggedIn ? '' : this._quizEmail();
      if (!this.customerLoggedIn && !email) {
        // Gast zonder bekend adres: niets om op te zoeken, lokale regels gelden.
        this.remoteVerdict = skip;
        return;
      }
      const identity = this.customerLoggedIn ? 'customer' : email;
      const cached = this._readEligibilityCache(identity);
      if (cached) {
        this.remoteVerdict = cached;
        this._applyEligibility();
        return;
      }
      try {
        const res = await fetch(this.eligibilityUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, recipe: this.productHandle, dog_name: this._quizDogName() })
        });
        const body = await res.json().catch(() => ({}));
        if (res.ok && body && body.ok) {
          this.remoteVerdict = {
            blocked_recipes: Array.isArray(body.blocked_recipes) ? body.blocked_recipes : [],
            block_all: !!body.block_all,
            reason: body.reason || ''
          };
          this._writeEligibilityCache(identity, this.remoteVerdict);
        } else {
          this.remoteVerdict = { error: true, blocked_recipes: [], block_all: false };
        }
      } catch (_) {
        this.remoteVerdict = { error: true, blocked_recipes: [], block_all: false };
      }
      this._applyEligibility();
    }

    // Naam van de hond uit de quiz-state, puur als context voor Flow en Klaviyo.
    // Geen beveiliging: een naam is zo verzonnen. Daarom gaat hij als eigenschap
    // mee op de orderregel en niet als beslisregel.
    _quizDogName() {
      try {
        const raw = localStorage.getItem('wolfork_quiz_state');
        if (!raw) return '';
        const state = JSON.parse(raw);
        return String((state && state.answers && state.answers.dog_name) || '').trim().slice(0, 60);
      } catch (_) {
        return '';
      }
    }

    // Splitst de plannen van deze variant in de intro-set en de gewone set en
    // levert er precies een van. Nooit allebei, anders kan de interval-match een
    // intro-plan kiezen voor iemand die daar geen recht op heeft.
    _filterIntro(list) {
      const all = Array.isArray(list) ? list : [];
      const wantsIntro = this._useIntro() && all.some(p => p.is_intro);
      this.usingIntro = wantsIntro;
      const picked = all.filter(p => !!p.is_intro === wantsIntro);
      return picked.length ? picked : all.filter(p => !p.is_intro);
    }

    _setPlansForVariant() {
      // Tiered pricing: elke variant heeft z'n eigen plan-set (eigen kortings-%).
      // Fallback: als de map leeg/afwezig is, houd de platte this.plans (backward-compatible).
      if (!this.plansByVariant || !Object.keys(this.plansByVariant).length) {
        this.plans = this._filterIntro(this.allPlans);
        return;
      }
      this.plans = this._filterIntro(this.plansByVariant[String(this.variantId)] || []);
    }

    _refreshSelectedPlan() {
      const manualPlan = this.manualPlanId
        ? this.plans.find(plan => plan.id === this.manualPlanId)
        : null;

      if (manualPlan) {
        this.selectedPlan = manualPlan;
      } else if (!this.plans.length || !this.variantGrams) {
        this.selectedPlan = null;
      } else if (!this.dailyGrams) {
        this.selectedPlan = this.plans[0];
      } else if (this.dailyGrams < this.minGrams || this.dailyGrams > this.maxGrams) {
        this.selectedPlan = null;
      } else {
        const qty = this.quantity || 1;
        // Match op voorraadduur minus 4 dagen buffer, zodat de volgende levering
        // aankomt vóórdat het voer op is (klant zit nooit zonder).
        const supplyDays = Math.max(1, Math.round((this.variantGrams * qty) / this.dailyGrams) - 4);
        this.selectedPlan = this.plans.reduce((best, plan) => {
          if (!best) return plan;
          return Math.abs(plan.days - supplyDays) < Math.abs(best.days - supplyDays) ? plan : best;
        }, null);
      }

      this._renderPlanSelect();
    }

    _isSubscriptionEnabled() {
      return !!this.selectedPlan;
    }

    _currentDiscountPct() {
      // Tier-% van de huidige variant (alle plannen van een variant delen hetzelfde %),
      // onafhankelijk van of er al een interval/plan gekozen is.
      if (this.plans && this.plans.length && this.plans[0].discount_pct > 0) {
        return this.plans[0].discount_pct;
      }
      return this.discountPct;
    }

    _subscriptionPriceCents() {
      const pct = (this.selectedPlan && this.selectedPlan.discount_pct) || this._currentDiscountPct();
      return Math.round(this.variantPriceCents * (100 - pct) / 100);
    }

    _renderOptionState() {
      // No disable gating - the subscription option is always selectable;
      // valid plan availability is shown via helper text + price sync.
    }

    // De dagdosis staat sinds het nieuwe ontwerp boven het keuzeblok en is altijd
    // zichtbaar: hij bepaalt zowel de bezorgfrequentie als de voorraadduur.
    _renderGramsRowVisibility() {
      const row = this.querySelector('[data-js-grams-row]');
      if (row) row.removeAttribute('hidden');
    }

    _renderPrices() {
      // Totaalprijzen, zoals in het ontwerp. De prijs per zak staat op de
      // hoeveelheidskaarten en in het hoofdprijsblok.
      const onetimeEl = this.querySelector('[data-js-onetime-price]');
      if (onetimeEl) onetimeEl.textContent = this._formatMoney(this.variantPriceCents);
      const subEl = this.querySelector('[data-js-subscription-price]');
      if (subEl) subEl.textContent = this._formatMoney(this._subscriptionPriceCents());

      this._renderIntroPricing();
      this._renderBenefits();
      this._renderSupplyNote();
      this._renderCartButtonPrice();

      const badgeEl = this.querySelector('[data-js-discount-badge]');
      if (badgeEl) {
        const pct = this._currentDiscountPct();
        if (pct > 0) badgeEl.textContent = this.discountBadgeTemplate.replace('[pct]', pct);
      }

      this._renderMainPrice();
    }

    // Reflecteer de gekozen optie (abonnement/los) in de grote prijs onder de titel.
    // Het thema herrendert dit blok volledig bij variant-wissel (innerHTML replace in
    // component-product-form.js), daarom passen we de korting na elke render opnieuw toe
    // i.p.v. eenmalig. Bij "los" of 0% korting blijft de originele serverprijs staan.
    _renderMainPrice() {
      const scope = this.closest('[data-js-product-component]') || document;
      const row = scope.querySelector('[data-update-block="price-compact"] [data-js-price-row]');
      if (!row) return;

      const priceEl = row.querySelector('.product-price');
      const perZakEl = row.querySelector('[data-js-per-zak]');
      const perZakAmountEl = perZakEl ? perZakEl.querySelector('[data-js-per-zak-amount]') : null;
      const active = this.option === 'subscription' && this._currentDiscountPct() > 0;

      if (!active) {
        const injected = row.querySelector('[data-js-wf-sub-price]');
        if (injected) injected.remove();
        row.classList.remove('wf-sub-price-active');
        if (perZakAmountEl) {
          const baseCents = parseInt(perZakEl.dataset.perZakCents, 10);
          if (!isNaN(baseCents)) perZakAmountEl.textContent = this._formatMoney(baseCents);
        }
        return;
      }

      const subCents = this._subscriptionPriceCents();
      const pct = this._currentDiscountPct();

      row.classList.add('wf-sub-price-active');

      let injected = row.querySelector('[data-js-wf-sub-price]');
      if (!injected) {
        injected = document.createElement('span');
        injected.className = 'wf-sub-price';
        injected.setAttribute('data-js-wf-sub-price', '');
        injected.innerHTML =
          '<span class="wf-sub-price__now"></span>' +
          '<span class="wf-sub-price__badge"></span>';
        if (priceEl) {
          priceEl.insertAdjacentElement('afterend', injected);
        } else {
          row.prepend(injected);
        }
      }

      injected.querySelector('.wf-sub-price__now').textContent = this._formatMoney(subCents);
      injected.querySelector('.wf-sub-price__badge').textContent = this.discountBadgeTemplate.replace('[pct]', pct);

      if (perZakAmountEl) {
        perZakAmountEl.textContent = this._formatMoney(this._perBagCents(subCents));
      }
    }

    _renderHelper() {
      this._renderFreqBanner();
      this._renderCartValidation();
      const helper = this.querySelector('[data-js-grams-helper]');
      const helperText = helper?.querySelector('[data-js-grams-helper-text]');
      if (!helper || !helperText) return;
      const defaultText = helper.dataset.defaultText
        || (helper.dataset.defaultText = helperText.textContent);
      const value = this.dailyGrams;

      if (this.option !== 'subscription') {
        helperText.textContent = defaultText;
        helper.classList.remove('wf-sub-toggle__grams-helper--error');
        return;
      }
      if (value === null || value === undefined) {
        helperText.textContent = this.i18n.helperRequired;
        helper.classList.add('wf-sub-toggle__grams-helper--error');
        return;
      }
      if (value < this.minGrams || value > this.maxGrams) {
        helperText.textContent = this.i18n.helperRange
          .replace('[min]', String(this.minGrams))
          .replace('[max]', String(this.maxGrams));
        helper.classList.add('wf-sub-toggle__grams-helper--error');
        return;
      }
      if (this.selectedPlan) {
        helperText.textContent = `${this._deliveryEvery(this._freqValue(this.selectedPlan.days))}.`;
        helper.classList.remove('wf-sub-toggle__grams-helper--error');
        return;
      }
      helperText.textContent = defaultText;
      helper.classList.remove('wf-sub-toggle__grams-helper--error');
    }

    _subscriptionNeedsGrams() {
      return this.option === 'subscription'
        && (!Number.isFinite(this.dailyGrams)
          || this.dailyGrams < this.minGrams
          || this.dailyGrams > this.maxGrams);
    }

    _renderCartValidation() {
      const invalid = this._subscriptionNeedsGrams();
      this.classList.toggle('wf-sub-toggle--grams-error', invalid);
      const input = this.querySelector('[data-js-grams-input]');
      if (input) {
        if (invalid) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      }

      if (!this.form) return;
      const button = this.form.querySelector('[data-js-product-add-to-cart]');
      if (!button) return;

      if (invalid) {
        button.disabled = true;
        button.classList.add('wf-grams-disabled');
        button.setAttribute('aria-disabled', 'true');
        button.dataset.wfGramsDisabled = 'true';
      } else if (button.dataset.wfGramsDisabled === 'true') {
        button.disabled = false;
        button.classList.remove('wf-grams-disabled');
        button.removeAttribute('aria-disabled');
        delete button.dataset.wfGramsDisabled;
      }
    }

    // Altijd in dagen, zoals op de huidige site ("elke 35 dagen", niet "5 weken").
    _freqValue(days) {
      if (days === 1) return this.i18n.frequencyDay;
      return this.i18n.frequencyDays.replace('[count]', String(days));
    }

    _deliveryEvery(frequency) {
      return this.i18n.deliveryEvery.replace('[freq]', frequency);
    }

    // Het percentage dat ELKE levering geldt. Bij een intro-plan is dat de tweede
    // prijsregel, bij een gewoon plan is het gelijk aan de korting zelf.
    _recurringPct() {
      if (this.selectedPlan && typeof this.selectedPlan.recurring_pct === 'number') {
        return this.selectedPlan.recurring_pct;
      }
      if (this.plans && this.plans.length && typeof this.plans[0].recurring_pct === 'number') {
        return this.plans[0].recurring_pct;
      }
      return this._currentDiscountPct();
    }

    _recurringPriceCents() {
      return Math.round((this.variantPriceCents * (100 - this._recurringPct())) / 100);
    }

    // Links van de abonnementsprijs staat altijd de relevante vergelijkprijs:
    // de gewone abonnementsprijs bij een intro, anders de losse aankoopprijs.
    _renderIntroPricing() {
      const isIntro = !!(this.selectedPlan && this.selectedPlan.is_intro) || !!this.usingIntro;
      const compareEl = this.querySelector('[data-js-sub-compare]');
      if (compareEl) {
        // Altijd de losse prijs doorgestreept, ook bij de intro: de 50% wordt
        // berekend op de losse verkoop.
        const comparePriceCents = this.variantPriceCents;
        if (comparePriceCents > this._subscriptionPriceCents()) {
          compareEl.textContent = this._formatMoney(comparePriceCents);
          compareEl.removeAttribute('hidden');
        } else {
          compareEl.setAttribute('hidden', '');
        }
      }
      const noteEl = this.querySelector('[data-js-sub-recurring-note]');
      if (noteEl) {
        if (isIntro) {
          noteEl.textContent = this.i18n.cyclePricing
            .replace('[first]', this._formatMoney(this._subscriptionPriceCents()))
            .replace('[recurring]', this._formatMoney(this._recurringPriceCents()));
          noteEl.removeAttribute('hidden');
        } else {
          noteEl.setAttribute('hidden', '');
        }
      }
    }

    _renderBenefits() {
      const el = this.querySelector('[data-js-benefit-discount]');
      if (!el || !el.dataset.template) return;
      el.textContent = el.dataset.template.replace('[pct]', this._recurringPct());
    }

    // "4 zakken hebben een gemiddelde duur van 24 dagen bij 124 gram per dag".
    // Zonder dagdosis of zakgewicht valt er niets te zeggen, dan blijft hij weg.
    _renderSupplyNote() {
      const el = this.querySelector('[data-js-supply-note]');
      if (!el || !el.dataset.template) return;
      const zakken = this._getVariantZakken(this.variantId);
      const qty = this.quantity || 1;
      const totalGrams = this.variantGrams * qty;
      if (!this.dailyGrams || !totalGrams || !zakken) {
        el.setAttribute('hidden', '');
        return;
      }
      const days = Math.round(totalGrams / this.dailyGrams);
      el.textContent = el.dataset.template
        .replace('[zakken]', String(zakken * qty))
        .replace('[dagen]', String(days))
        .replace('[gram]', String(this.dailyGrams));
      el.removeAttribute('hidden');
    }

    // Prijs in de winkelmandknop: het bedrag dat daadwerkelijk in de cart belandt,
    // dus de gekozen optie maal het aantal. Het thema vult deze span bij een
    // variantwissel opnieuw met de kale variantprijs; krown:variant:change volgt
    // daarna, dus deze schrijfactie wint.
    _renderCartButtonPrice() {
      const scope = this.form || this.closest('[data-js-product-component]') || document;
      const el = scope.querySelector('[data-js-cart-price]');
      if (!el) return;
      const unit = this.option === 'subscription' && this.selectedPlan
        ? this._subscriptionPriceCents()
        : this.variantPriceCents;
      if (!unit) {
        el.textContent = '';
        return;
      }
      el.textContent = ' - ' + this._formatMoney(unit * (this.quantity || 1));
    }

    _renderFreqBanner() {
      const banner = this.querySelector('[data-js-freq-banner]');
      if (!banner) return;
      if (this.option !== 'subscription') {
        banner.setAttribute('hidden', '');
        return;
      }
      banner.removeAttribute('hidden');
      const isEmpty = !this.dailyGrams;
      banner.classList.toggle('wf-sub-toggle__schedule--empty', isEmpty);
      const normalIcon = banner.querySelector('[data-js-schedule-icon]');
      const emptyIcon = banner.querySelector('[data-js-empty-schedule-icon]');
      const normalContent = banner.querySelector('[data-js-schedule-content]');
      const emptyContent = banner.querySelector('[data-js-empty-schedule-content]');
      if (normalIcon) normalIcon.hidden = isEmpty;
      if (emptyIcon) emptyIcon.hidden = !isEmpty;
      if (normalContent) normalContent.hidden = isEmpty;
      if (emptyContent) emptyContent.hidden = !isEmpty;
      if (isEmpty) return;
      if (!this.selectedPlan) return; // keep server-rendered default frequency
      const freq = this._freqValue(this.selectedPlan.days);
      const titleEl = banner.querySelector('[data-js-freq-title]');
      if (titleEl) titleEl.textContent = this._deliveryEvery(freq);
      const subEl = banner.querySelector('[data-js-freq-sub]');
      if (subEl) {
        if (subEl.dataset.template) {
          subEl.textContent = subEl.dataset.template.replace('[freq]', freq);
        }
      }
    }

    _renderPlanSelect() {
      const select = this.querySelector('[data-js-plan-select]');
      if (!select) return;

      const selectablePlans = this.plans.filter((plan, index, plans) => (
        plans.findIndex(item => item.days === plan.days) === index
      )).sort((a, b) => a.days - b.days);
      const signature = selectablePlans.map(plan => `${plan.id}:${plan.days}`).join('|');
      if (select.dataset.signature !== signature) {
        select.replaceChildren(...selectablePlans.map(plan => {
          const option = document.createElement('option');
          option.value = String(plan.id);
          option.textContent = this._freqValue(plan.days);
          return option;
        }));
        select.dataset.signature = signature;
      }

      select.disabled = !this.plans.length;
      if (this.selectedPlan) {
        select.value = String(this.selectedPlan.id);
      }
    }

    _hydrateGramsInput() {
      const input = this.querySelector('[data-js-grams-input]');
      if (!input) return;
      if (this.dailyGrams) {
        input.value = String(this.dailyGrams);
      }
    }

    _syncSellingPlanInput() {
      if (!this.form) {
        this.form = this.formId ? document.getElementById(this.formId) : null;
      }
      if (!this.form) return;

      let input = this.form.querySelector('input[name="selling_plan"][data-js-wf-sub]');
      if (this.option === 'subscription' && this.selectedPlan) {
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          input.dataset.jsWfSub = '';
          this.form.appendChild(input);
        }
        input.value = String(this.selectedPlan.id);
      } else if (input) {
        input.remove();
      }
      this._syncIntroProperties();
      this._lockQuantityForIntro();
    }

    // Orderregel-eigenschappen voor laag 3 (Flow-controle na de order) en Klaviyo.
    // Underscore-prefix: Shopify verbergt ze in cart en checkout. Alleen aanwezig
    // als er echt een intro-plan in het formulier staat.
    _syncIntroProperties() {
      if (!this.form) return;
      const active = this.option === 'subscription' && this.selectedPlan && !!this.selectedPlan.is_intro;
      const fields = {
        'properties[_wf_intro]': active ? '1' : null,
        'properties[_wf_recipe]': active ? this.productHandle : null,
        'properties[_wf_dog]': active ? this._quizDogName() : null,
      };
      Object.entries(fields).forEach(([name, value]) => {
        let el = this.form.querySelector('input[name="' + name + '"][data-js-wf-intro]');
        if (!active || !value) {
          if (el) el.remove();
          return;
        }
        if (!el) {
          el = document.createElement('input');
          el.type = 'hidden';
          el.name = name;
          el.dataset.jsWfIntro = '';
          this.form.appendChild(el);
        }
        el.value = value;
      });
    }

    _findCurrentVariantId() {
      if (!this.form) return null;
      const idInput = this.form.querySelector('input[name="id"]');
      const fromForm = idInput ? parseInt(idInput.value, 10) : NaN;
      if (!isNaN(fromForm) && fromForm > 0) return fromForm;
      return this.variants[0] ? this.variants[0].id : null;
    }

    _readGramsFromUrl() {
      try {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('grams');
        if (!raw) return null;
        const n = parseInt(raw, 10);
        if (isNaN(n) || n < this.minGrams || n > this.maxGrams) return null;
        return n;
      } catch (_) {
        return null;
      }
    }

    _cleanUrlParam() {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('grams');
        window.history.replaceState({}, '', url.toString());
      } catch (_) {}
    }

    _getVariantGrams(variantId) {
      const v = this.variants.find(x => x.id === variantId);
      if (!v) return 0;
      if (v.zakken && v.zakken > 0) return v.zakken * 2100;
      return v.grams || 0;
    }

    _getVariantPrice(variantId) {
      const v = this.variants.find(x => x.id === variantId);
      return v ? v.price : 0;
    }

    _getVariantZakken(variantId) {
      const v = this.variants.find(x => x.id === variantId);
      return v && v.zakken ? v.zakken : 0;
    }

    // Prijs per zak voor weergave: totaalprijs / aantal zakken. Alleen voor de "per zak"-
    // labels; de echte totaalprijs (event/cart) blijft ongedeeld.
    _perBagCents(cents) {
      const zakken = this._getVariantZakken(this.variantId);
      return zakken > 0 ? Math.round(cents / zakken) : cents;
    }

    _formatMoney(cents) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const format = this.moneyFormat || '€{{amount}}';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholderRegex);
      if (!match) return format;

      const formatWithDelimiters = (number, precision, thousands, decimal) => {
        thousands = thousands || '.';
        decimal = decimal || ',';
        if (isNaN(number) || number === null) return '0';
        const fixed = (number / 100.0).toFixed(precision);
        const parts = fixed.split('.');
        const main = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const fraction = parts[1] ? decimal + parts[1] : '';
        return main + fraction;
      };

      let value = '';
      switch (match[1]) {
        case 'amount':                       value = formatWithDelimiters(cents, 2); break;
        case 'amount_no_decimals':           value = formatWithDelimiters(cents, 0); break;
        case 'amount_with_comma_separator':  value = formatWithDelimiters(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
        default:                              value = formatWithDelimiters(cents, 2);
      }
      return format.replace(placeholderRegex, value);
    }
  }

  if (typeof customElements.get('wolfork-subscription-toggle') === 'undefined') {
    customElements.define('wolfork-subscription-toggle', WolforkSubscriptionToggle);
  }
}
